/**
 * columnar.ts
 * ===========
 * Compact typed-array representation of OHLCV candle data.
 * Eliminates the N×-copy Candle[] problem by storing all six fields
 * as contiguous Float64Arrays (one allocation per field).
 *
 * Used throughout the new data pipeline:
 *   parse → CandleColumns → OPFS → chart-data-service → windowed Candle[] display slices
 */

import type { Candle } from "@/lib/lytick/replay-utils";

// ─── Core Type ───────────────────────────────────────────────────────────────

export interface CandleColumns {
  time: Float64Array; // ms timestamps (UTC)
  open: Float64Array;
  high: Float64Array;
  low: Float64Array;
  close: Float64Array;
  volume: Float64Array;
  length: number;
}

// ─── Growable Builder ────────────────────────────────────────────────────────

const FIELD_NAMES = ["time", "open", "high", "low", "close", "volume"] as const;

export class ColumnarBuilder {
  private capacity: number;
  time: Float64Array;
  open: Float64Array;
  high: Float64Array;
  low: Float64Array;
  close: Float64Array;
  volume: Float64Array;
  length: number = 0;

  constructor(initialCapacity = 8192) {
    this.capacity = initialCapacity;
    this.time = new Float64Array(initialCapacity);
    this.open = new Float64Array(initialCapacity);
    this.high = new Float64Array(initialCapacity);
    this.low = new Float64Array(initialCapacity);
    this.close = new Float64Array(initialCapacity);
    this.volume = new Float64Array(initialCapacity);
  }

  append(ts: number, o: number, h: number, l: number, c: number, v: number): void {
    if (this.length === this.capacity) this._grow();
    const i = this.length++;
    this.time[i] = ts;
    this.open[i] = o;
    this.high[i] = h;
    this.low[i] = l;
    this.close[i] = c;
    this.volume[i] = v;
  }

  finalize(): CandleColumns {
    const n = this.length;
    return {
      time: this.time.slice(0, n),
      open: this.open.slice(0, n),
      high: this.high.slice(0, n),
      low: this.low.slice(0, n),
      close: this.close.slice(0, n),
      volume: this.volume.slice(0, n),
      length: n,
    };
  }

  private _grow(): void {
    const newCap = this.capacity * 2;
    for (const f of FIELD_NAMES) {
      const next = new Float64Array(newCap);
      next.set(this[f]);
      (this as Record<string, unknown>)[f] = next;
    }
    this.capacity = newCap;
  }
}

// ─── Candle <-> Columns Interop ──────────────────────────────────────────────

/** Read a single candle from a CandleColumns at index i. */
export function candleAt(cols: CandleColumns, i: number): Candle {
  return {
    timestamp: cols.time[i],
    open: cols.open[i],
    high: cols.high[i],
    low: cols.low[i],
    close: cols.close[i],
    volume: cols.volume[i],
  };
}

/** Convert a half-open range [start, end) of CandleColumns to a Candle[]. */
export function sliceToCandles(
  cols: CandleColumns,
  start: number,
  end: number,
): Candle[] {
  const s = Math.max(0, start);
  const e = Math.min(end, cols.length);
  if (e <= s) return [];
  const result: Candle[] = new Array(e - s);
  for (let i = s; i < e; i++) {
    result[i - s] = {
      timestamp: cols.time[i],
      open: cols.open[i],
      high: cols.high[i],
      low: cols.low[i],
      close: cols.close[i],
      volume: cols.volume[i],
    };
  }
  return result;
}

// ─── Binary Search ───────────────────────────────────────────────────────────

/**
 * Binary search: first index where cols.time[i] >= ts.
 * Returns cols.length if all entries are < ts.
 */
export function searchTime(cols: CandleColumns, ts: number): number {
  let lo = 0;
  let hi = cols.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (cols.time[mid] < ts) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/**
 * Binary search: index of the entry nearest to ts (for replay seek).
 * Equivalent to seekToNearestTimestamp but operating on CandleColumns.
 */
export function searchTimeNearest(cols: CandleColumns, ts: number): number {
  if (cols.length === 0) return 0;
  const idx = searchTime(cols, ts);
  if (idx === 0) return 0;
  if (idx >= cols.length) return cols.length - 1;
  const before = cols.time[idx - 1];
  const after = cols.time[idx];
  return ts - before <= after - ts ? idx - 1 : idx;
}

// ─── Serialization ───────────────────────────────────────────────────────────

// Binary layout: [magic:4][version:1][pad:3][length:4][pad:4][6 × Float64Array data]
// magic = 'COLV' = 0x434F4C56, version = 1
const MAGIC = 0x434f4c56;
const COL_VERSION = 1;
const HEADER_SIZE = 16;

export function serializeColumns(cols: CandleColumns): ArrayBuffer {
  const n = cols.length;
  const buf = new ArrayBuffer(HEADER_SIZE + 6 * n * 8);
  const view = new DataView(buf);
  view.setUint32(0, MAGIC, true);
  view.setUint8(4, COL_VERSION);
  view.setUint32(8, n, true);

  let offset = HEADER_SIZE;
  for (const f of FIELD_NAMES) {
    new Float64Array(buf, offset, n).set(cols[f]);
    offset += n * 8;
  }
  return buf;
}

export function deserializeColumns(buf: ArrayBuffer): CandleColumns {
  const view = new DataView(buf);
  if (view.getUint32(0, true) !== MAGIC) throw new Error("Invalid columnar buffer magic");
  const n = view.getUint32(8, true);

  let offset = HEADER_SIZE;
  const result: Partial<CandleColumns> & { length: number } = { length: n };
  for (const f of FIELD_NAMES) {
    // slice to detach from the source buffer (safe to GC the original)
    result[f] = new Float64Array(buf.slice(offset, offset + n * 8));
    offset += n * 8;
  }
  return result as CandleColumns;
}
